import { SelectUnit } from '../../SelectUnits/types'
import { getUnitsIndividual } from '../selectors'
import { RootState } from '../../../store'
import { createAsyncAction } from 'async-selector-kit'
import cloneDeep from 'lodash/cloneDeep'
import { INDIVIDUAL_UNITS } from '../../../firebase/config'
import { UnitIndividual, UnitMaster } from '../reducer'
import { onError } from '../../Profile/selectors/api/utils'

/** Writes one individual unit to Firebase by id. Exported for use by master commands (new-individual creation). */
export const setIndividualUnitFirebase = async (model: UnitIndividual) =>
  window.firebase
    .database()
    .ref()
    .child(`${INDIVIDUAL_UNITS}/${model.id}`)
    .set(model)

/** Shape used when freeing an individual unit (clearing customer/subscription). */
export type FreedUnitIndividual = Pick<
  UnitIndividual,
  'id' | 'isClimate' | 'isCovered' | 'masterId' | 'size' | 'unitNumber'
>

export type StoreLike = {
  getState: () => RootState
  dispatch: (action: unknown) => void
}

/**
 * Core logic: find individual by unitNumber, merge customer/subscription, write to Firebase.
 * Exported for testing.
 */
export async function executeUpdateUnitIndividual(
  store: StoreLike,
  unit: SelectUnit
): Promise<void> {
  const individualUnits = getUnitsIndividual(store.getState() as RootState)
  const objIndividual = individualUnits.find(
    (y) => unit.unitNumber === y.unitNumber
  )

  if (objIndividual == null) return

  const merged = cloneDeep(objIndividual)
  merged.customerProfileId = unit.customerProfileId
  merged.subscriptionId = unit.subscriptionId
  try {
    await setIndividualUnitFirebase(merged)
  } catch (error) {
    onError('failed to update firebase')
  }
}

/**
 * Core logic: write full UnitIndividual to Firebase. Exported for testing.
 */
export async function executeChangeUnitIndividual(
  unit: UnitIndividual
): Promise<void> {
  try {
    await setIndividualUnitFirebase(unit)
  } catch (error) {
    throw new Error('Failed to update firebase')
  }
}

/**
 * Core logic: find individual by subscriptionId + customerProfileId, write freed shape to Firebase.
 * Exported for testing.
 */
export async function executeCancelRequestUnitIndividual(
  store: StoreLike,
  unit: SelectUnit
): Promise<void> {
  const individualUnits = getUnitsIndividual(store.getState() as RootState)
  const _unit = individualUnits.find(
    (y) =>
      y.subscriptionId === unit.subscriptionId &&
      y.customerProfileId === unit.customerProfileId
  )

  if (_unit == null) return

  const freed: FreedUnitIndividual = {
    id: _unit.id,
    isClimate: _unit.isClimate,
    isCovered: _unit.isCovered,
    masterId: _unit.masterId,
    size: _unit.size,
    unitNumber: _unit.unitNumber,
  }
  try {
    await setIndividualUnitFirebase(freed as UnitIndividual)
  } catch (error) {
    onError('failed to update firebase')
  }
}

export const [
  updateUnitIndividual,
  updateUnitIndividualLoading,
  updateUnitIndividualError,
] = createAsyncAction(
  {
    id: 'update-unit-individual',
    async: (store) => async (unit: SelectUnit) => {
      await executeUpdateUnitIndividual(store, unit)
    },
  },
  []
)

export const [
  changeUnitIndividual,
  changeUnitIndividualLoading,
  changeUnitIndividualError,
] = createAsyncAction(
  {
    id: 'change-unit-individual',
    async: () => async (unit: UnitIndividual) => {
      await executeChangeUnitIndividual(unit)
    },
  },
  []
)

export const [
  cancelRequestUnitIndividual,
  cancelRequestUnitIndividualLoading,
  cancelRequestUnitIndividualError,
] = createAsyncAction(
  {
    id: 'cancel-request-unit-individual',
    async: (store) => async (unit: SelectUnit) => {
      await executeCancelRequestUnitIndividual(store, unit)
    },
  },
  []
)
